import { useState, type CSSProperties, type ReactElement } from 'react';
import type { WidgetProps } from '@rjsf/utils';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Extensions } from '@tiptap/core';
import { Placeholder, StarterKit, TiptapLink, TiptapUnderline } from 'novel';
import TextAlign from '@tiptap/extension-text-align';

import AlignableImage from 'app/shared-components/forms/RichTextEditor/extensions/AlignableImage';
import RichTextEditorToolbar from 'app/shared-components/forms/RichTextEditor/RichTextEditorToolbar';

import { useUploadAsset } from '../../Pages/Section/hooks/useUploadAsset';

/**
 * Set per field through `ui:options.toolbar` in the section type's uiSchema. `basic` is bold, italic
 * and paragraphs, for short copy such as a hero subtitle; `full` is the same toolbar as service
 * descriptions (headings, underline, alignment, lists, blockquote, images).
 */
export type RichTextToolbar = 'basic' | 'full';

const DEFAULT_MIN_HEIGHT: Record<RichTextToolbar, number> = { basic: 96, full: 200 };

/**
 * The editor always starts its HTML with a block: a paragraph, heading, list, blockquote or image.
 * Anything else is plain text from the textarea this field used to be, even when it happens to
 * contain something tag-like, and is escaped so it stays literal.
 */
const EDITOR_HTML = /^\s*<(?:p|h[1-3]|ul|ol|blockquote|img)\b/i;

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (char) => ESCAPES[char]);
}

/**
 * The HTML the editor is seeded with. Values saved by the rich text editor are HTML and load as
 * they are; values saved by the textarea this field used to be are plain text, so each line becomes
 * a paragraph and the newlines the textarea allowed survive the switch.
 */
export function toEditorHtml(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '';
  if (EDITOR_HTML.test(value)) return value;

  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}

/** The value handed to the form: the editor's HTML, or `undefined` for a document with nothing in it. */
export function serialise(editor: { getHTML(): string; getText(): string }): string | undefined {
  const html = editor.getHTML();
  const hasImage = html.includes('<img');
  // A whitespace-only document counts as empty so optional fields stay unset.
  if (!hasImage && !editor.getText().trim()) return undefined;
  return html;
}

export function buildExtensions(toolbar: RichTextToolbar, placeholder?: string): Extensions {
  const shared = [Placeholder.configure({ placeholder: placeholder ?? '' })];

  if (toolbar === 'basic') {
    return [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        strike: false,
        horizontalRule: false,
      }),
      ...shared,
    ];
  }

  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      bulletList: { keepMarks: true, keepAttributes: false },
      orderedList: { keepMarks: true, keepAttributes: false },
      codeBlock: false,
      code: false,
      strike: false,
      horizontalRule: false,
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    AlignableImage,
    TiptapLink.configure({ openOnClick: false }),
    TiptapUnderline,
    ...shared,
  ];
}

// Descendant styling for what tiptap renders inside `.ProseMirror`; the min height comes from the
// field's ui:options through a CSS variable.
const EDITOR_CLASSES = [
  'vtwo:w-full vtwo:overflow-hidden vtwo:rounded-lg vtwo:border vtwo:bg-white',
  'vtwo:[&_.ProseMirror]:min-h-[var(--rich-text-min-height)] vtwo:[&_.ProseMirror]:cursor-text',
  'vtwo:[&_.ProseMirror]:px-3 vtwo:[&_.ProseMirror]:py-2 vtwo:[&_.ProseMirror]:outline-none',
  'vtwo:[&_.ProseMirror_p.is-editor-empty:first-of-type]:before:pointer-events-none',
  'vtwo:[&_.ProseMirror_p.is-editor-empty:first-of-type]:before:float-left',
  'vtwo:[&_.ProseMirror_p.is-editor-empty:first-of-type]:before:h-0',
  'vtwo:[&_.ProseMirror_p.is-editor-empty:first-of-type]:before:text-gray-400',
  'vtwo:[&_.ProseMirror_p.is-editor-empty:first-of-type]:before:content-[attr(data-placeholder)]',
  'vtwo:[&_.ProseMirror>*+*]:mt-2',
  'vtwo:[&_.ProseMirror_h1]:text-2xl vtwo:[&_.ProseMirror_h1]:font-bold vtwo:[&_.ProseMirror_h1]:leading-tight',
  'vtwo:[&_.ProseMirror_h2]:text-xl vtwo:[&_.ProseMirror_h2]:font-bold vtwo:[&_.ProseMirror_h2]:leading-tight',
  'vtwo:[&_.ProseMirror_h3]:text-lg vtwo:[&_.ProseMirror_h3]:font-semibold vtwo:[&_.ProseMirror_h3]:leading-snug',
  'vtwo:[&_.ProseMirror_ul]:list-disc vtwo:[&_.ProseMirror_ul]:pl-6',
  'vtwo:[&_.ProseMirror_ol]:list-decimal vtwo:[&_.ProseMirror_ol]:pl-6',
  'vtwo:[&_.ProseMirror_li+li]:mt-1',
  'vtwo:[&_.ProseMirror_blockquote]:border-l-[3px] vtwo:[&_.ProseMirror_blockquote]:border-gray-200',
  'vtwo:[&_.ProseMirror_blockquote]:pl-3 vtwo:[&_.ProseMirror_blockquote]:text-gray-600',
  'vtwo:[&_.ProseMirror_img]:block vtwo:[&_.ProseMirror_img]:h-auto',
  'vtwo:[&_.ProseMirror_img]:max-w-full vtwo:[&_.ProseMirror_img]:rounded-lg',
  'vtwo:[&_.ProseMirror_img[data-align=left]]:float-left vtwo:[&_.ProseMirror_img[data-align=left]]:mr-4',
  'vtwo:[&_.ProseMirror_img[data-align=right]]:float-right vtwo:[&_.ProseMirror_img[data-align=right]]:ml-4',
  'vtwo:[&_.ProseMirror_img[data-align=center]]:mx-auto vtwo:[&_.ProseMirror_img[data-align=center]]:my-2',
  'vtwo:[&_.ProseMirror_img.ProseMirror-selectednode]:outline-2',
  'vtwo:[&_.ProseMirror_img.ProseMirror-selectednode]:outline-offset-2',
  'vtwo:[&_.ProseMirror_img.ProseMirror-selectednode]:outline-teal-600',
].join(' ');

interface ImageMenuAnchor {
  type: 'align' | 'size';
  element: HTMLElement;
}

export function RichTextWidget({
  id,
  value,
  onChange,
  disabled = false,
  readonly = false,
  options,
  placeholder,
  rawErrors = [],
}: WidgetProps): ReactElement | null {
  const toolbar: RichTextToolbar = options.toolbar === 'basic' ? 'basic' : 'full';
  const minHeight =
    typeof options.minHeight === 'number' ? options.minHeight : DEFAULT_MIN_HEIGHT[toolbar];
  const editable = !disabled && !readonly;

  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { mutateAsync: uploadAsset } = useUploadAsset();

  const [selectedImage, setSelectedImage] = useState(false);
  const [imageMenuAnchor, setImageMenuAnchor] = useState<ImageMenuAnchor | null>(null);

  // Seeded once from the stored value; the editor is uncontrolled thereafter, so the form only
  // learns about what the author actually changes (a legacy plain-text value is left as it is
  // until edited).
  const editor = useEditor({
    extensions: buildExtensions(toolbar, placeholder),
    content: toEditorHtml(value),
    editable,
    onUpdate: ({ editor: instance }) => onChange(serialise(instance)),
    onSelectionUpdate: ({ editor: instance }) => setSelectedImage(instance.isActive('image')),
  });

  if (!editor) return null;

  const onImageUpload =
    toolbar === 'full'
      ? async (file: File) => (await uploadAsset({ file, organisationId })).url
      : undefined;

  const handleImageAlign = (align: 'left' | 'center' | 'right') => {
    editor.chain().focus().updateAttributes('image', { align }).run();
    setImageMenuAnchor(null);
  };

  const handleImageSize = (width: string) => {
    editor.chain().focus().updateAttributes('image', { width }).run();
    setImageMenuAnchor(null);
  };

  const editorStyle = { '--rich-text-min-height': `${minHeight}px` } as CSSProperties;

  return (
    <div
      id={id}
      className={clsx(
        EDITOR_CLASSES,
        rawErrors.length > 0 ? 'vtwo:border-red-500' : 'vtwo:border-gray-200'
      )}
      style={editorStyle}
    >
      {editable && (
        <div className="vtwo:border-b vtwo:border-gray-200">
          <RichTextEditorToolbar
            editor={editor}
            variant={toolbar}
            onImageUpload={onImageUpload}
            selectedImage={selectedImage}
            imageMenuAnchor={imageMenuAnchor}
            setImageMenuAnchor={setImageMenuAnchor}
            handleImageAlign={handleImageAlign}
            handleImageSize={handleImageSize}
          />
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
